import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import '@material/web/dialog/dialog.js'
import '@material/web/button/text-button.js'
import '@material/web/button/filled-button.js'
import type { MdDialog } from '@material/web/dialog/dialog.js'

@customElement('confirm-dialog')
export class ConfirmDialog extends LitElement {
  static override styles = css`
    :host {
      position: fixed;
      z-index: 3000;
      pointer-events: none;
    }

    md-dialog {
      --md-dialog-container-color: #1e293b;
      --md-dialog-headline-color: white;
      --md-dialog-supporting-text-color: rgba(255, 255, 255, 0.8);
      --md-sys-color-outline: rgba(255, 255, 255, 0.2);
      --md-dialog-container-shape: 12px;
      pointer-events: all;
    }

    md-text-button {
      --md-text-button-label-text-color: rgba(255, 255, 255, 0.7);
      --md-text-button-hover-label-text-color: white;
    }

    md-filled-button {
      --md-filled-button-container-color: #ef4444;
      --md-filled-button-label-text-color: white;
      --md-filled-button-hover-label-text-color: white;
    }
  `

  @property({ type: String })
  headline = 'Confirm'

  @property({ type: String })
  message = 'Are you sure?'

  @property({ type: String })
  confirmLabel = 'Delete'

  @property({ type: String })
  cancelLabel = 'Cancel'

  private resolvePromise?: ((confirmed: boolean) => void) | undefined
  private animationsConfigured = false

  private configureAnimations(): void {
    if (this.animationsConfigured) {
      return
    }
    const dialog = this.shadowRoot?.querySelector('md-dialog') as MdDialog | null
    if (!dialog) {
      return
    }
    this.animationsConfigured = true

    const dur = 100
    const easing = 'cubic-bezier(0.2, 0, 0, 1)'

    dialog.getOpenAnimation = () => ({
      dialog: [
        [
          [
            { transform: 'scale(0.85)', opacity: 0 },
            { transform: 'scale(1)', opacity: 1 },
          ],
          { duration: dur, easing },
        ],
      ],
      scrim: [[[{ opacity: 0 }, { opacity: 0.32 }], { duration: dur, easing: 'linear' }]],
      container: [
        [
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: dur * 0.5, easing: 'linear', pseudoElement: '::before' },
        ],
      ],
      headline: [
        [
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: dur, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
      content: [
        [
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: dur, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
      actions: [
        [
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: dur, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
    })

    dialog.getCloseAnimation = () => ({
      dialog: [
        [
          [
            { transform: 'scale(1)', opacity: 1 },
            { transform: 'scale(0.9)', opacity: 0 },
          ],
          { duration: 80, easing: 'cubic-bezier(0.4, 0, 1, 1)' },
        ],
      ],
      scrim: [[[{ opacity: 0.32 }, { opacity: 0 }], { duration: 80, easing: 'linear' }]],
      container: [
        [
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: 50, easing: 'linear', pseudoElement: '::before' },
        ],
      ],
      headline: [
        [
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: 50, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
      content: [
        [
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: 50, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
      actions: [
        [
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: 50, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
    })
  }

  protected override firstUpdated(): void {
    this.configureAnimations()
  }

  async show(): Promise<boolean> {
    const dialog = this.shadowRoot?.querySelector('md-dialog') as MdDialog | null
    if (!dialog) {
      return false
    }
    this.configureAnimations()
    return new Promise<boolean>(resolve => {
      this.resolvePromise = resolve
      void dialog.show()
    })
  }

  private handleClose(e: Event): void {
    const dialog = e.target as MdDialog
    this.resolvePromise?.(dialog.returnValue === 'confirm')
    this.resolvePromise = undefined
  }

  override render(): unknown {
    return html`
      <md-dialog @closed=${this.handleClose}>
        <div slot="headline">${this.headline}</div>
        <div slot="content">${this.message}</div>
        <div slot="actions">
          <md-text-button
            @click=${(): void => {
              void (this.shadowRoot?.querySelector('md-dialog') as MdDialog).close('cancel')
            }}
          >
            ${this.cancelLabel}
          </md-text-button>
          <md-filled-button
            @click=${(): void => {
              void (this.shadowRoot?.querySelector('md-dialog') as MdDialog).close('confirm')
            }}
          >
            ${this.confirmLabel}
          </md-filled-button>
        </div>
      </md-dialog>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'confirm-dialog': ConfirmDialog
  }
}
